import { Resource, RoomType } from '../../types';

// Mock room types and resources data

export const roomTypes: RoomType[] = [
  'classroom',
  'laboratory',
  'ict',
  'library',
  'hall',
  'sports',
  'dining',
  'office',
  'medical',
  'storage',
  'changing',
  'study',
  'specialist',
];

export const roomTypeLabels: Record<RoomType, string> = {
  classroom: 'Classroom',
  laboratory: 'Laboratory',
  ict: 'ICT Suite',
  library: 'Library',
  hall: 'Hall',
  sports: 'Sports Facility',
  dining: 'Dining Area',
  office: 'Office',
  medical: 'Medical Room',
  storage: 'Storage',
  changing: 'Changing Room',
  study: 'Study Area',
  specialist: 'Specialist Room',
};

export const resourceTypes = ['teaching', 'ict', 'av', 'specialist', 'sports', 'furniture', 'communication'] as const;

export type ResourceType = (typeof resourceTypes)[number];

export const commonResources: Record<string, Omit<Resource, 'quantity'>> = {
  whiteboard: {
    id: 'res-whiteboard',
    name: 'Whiteboard',
    type: 'teaching',
  },
  projector: {
    id: 'res-projector',
    name: 'Projector',
    type: 'av',
  },
  computer: {
    id: 'res-computer',
    name: 'Computer',
    type: 'ict',
  },
  printer: {
    id: 'res-printer',
    name: 'Printer',
    type: 'ict',
  },
  microscope: {
    id: 'res-microscope',
    name: 'Microscope',
    type: 'specialist',
  },
  fumeHood: {
    id: 'res-fume-hood',
    name: 'Fume Hood',
    type: 'specialist',
  },
  gasTap: {
    id: 'res-gas-tap',
    name: 'Gas Tap',
    type: 'specialist',
  },
  audioSystem: {
    id: 'res-audio-system',
    name: 'Audio System',
    type: 'av',
  },
  piano: {
    id: 'res-piano',
    name: 'Piano',
    type: 'specialist',
  },
  easel: {
    id: 'res-easel',
    name: 'Easel',
    type: 'specialist',
  },
  sportsEquipment: {
    id: 'res-sports-equipment',
    name: 'Sports Equipment',
    type: 'sports',
  },
  desk: {
    id: 'res-desk',
    name: 'Desk',
    type: 'furniture',
  },
  chair: {
    id: 'res-chair',
    name: 'Chair',
    type: 'furniture',
  },
  locker: {
    id: 'res-locker',
    name: 'Locker',
    type: 'furniture',
  },
  phone: {
    id: 'res-phone',
    name: 'Phone',
    type: 'communication',
  },
};

// Room capacity guidelines by type
export const roomCapacityGuidelines: Record<RoomType, { min: number; max: number; typical: number }> = {
  classroom: { min: 20, max: 35, typical: 30 },
  laboratory: { min: 20, max: 30, typical: 28 },
  ict: { min: 20, max: 35, typical: 30 },
  library: { min: 40, max: 100, typical: 60 },
  hall: { min: 100, max: 500, typical: 300 },
  sports: { min: 30, max: 80, typical: 60 },
  dining: { min: 100, max: 400, typical: 250 },
  office: { min: 1, max: 20, typical: 6 },
  medical: { min: 1, max: 8, typical: 4 },
  storage: { min: 0, max: 0, typical: 0 },
  changing: { min: 20, max: 60, typical: 40 },
  study: { min: 20, max: 60, typical: 40 },
  specialist: { min: 15, max: 30, typical: 25 },
};

// Resource requirements by room type
export const requiredResourcesByRoomType: Record<RoomType, string[]> = {
  classroom: ['whiteboard', 'projector'],
  laboratory: ['whiteboard', 'fumeHood'],
  ict: ['computer', 'printer'],
  library: ['computer', 'printer', 'desk', 'chair'],
  hall: ['audioSystem'],
  sports: ['sportsEquipment'],
  dining: [],
  office: ['desk', 'chair', 'phone'],
  medical: [],
  storage: [],
  changing: ['locker'],
  study: ['desk', 'chair'],
  specialist: ['whiteboard'],
};
